import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  LeadQuery,
  LeadSupplierBuyerNoticeSummaryQuery,
  SupplierBuyerNoticeSummaryRequest,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const LEAD_QUERY = graphql(`
  query lead($id: String!) {
    lead(id: $id) {
      id
      name
      description
      descriptionSources {
        type
        id
        url
        title
        chunkNumber
        documentType
        filePath
        isDeleted
        citationId
        locations {
          pageNumber
          bbox {
            x
            y
            width
            height
          }
        }
      }
      value
      createdAt
      updatedAt
      expiryDate
      contactSuggestionsRequestedAt
      assignedTo {
        guid
        firstName
        lastName
        active
      }
      stage {
        id
        name
        color
        iconName
        iconColor
        transitionGroup
      }
      buyers {
        id
        name
        logoUrl
      }
      suppliers {
        id
        name
        logoUrl
      }
      customBuyers {
        id
        name
      }
      comments {
        id
        content
        author {
          guid
          firstName
          lastName
          active
        }
        createdAt
        updatedAt
      }
      contacts {
        id
        title
        firstName
        lastName
        jobTitle
        seniority
        jobFunction
        email
        phone
        mobilePhoneNumbers
        directPhoneNumbers
        linkedinUrl
        oscarOrgName
        source
        cognismContactId
        buyer {
          id
          name
        }
      }
      suggestedContacts {
        id
        source
        contactId
        redeemId
        fullName
        jobTitle
        seniority
        jobFunction
        email
        phone
        mobilePhoneNumbers
        directPhoneNumbers
        relevance
        reason
        buyer {
          id
          name
          logoUrl
          categoryAssignments {
            categoryName
            parentCategoryName
          }
        }
      }
      documents {
        id
        title
        publishedAt
        category
        buyers {
          id
          name
        }
      }
      frameworks {
        id
        title
        stage
        tenderPeriod {
          endDate
        }
        contractPeriod {
          endDate
        }
        buyers {
          id
          name
        }
      }
      notices {
        guid
        name
        stagev2
        expiryDate
        closeDate
        publishDate
        procurementStage {
          id
        }
        lots {
          suppliers {
            guid
            name
          }
        }
        buyers {
          guid
          name
          logoUrl
          createdAt
        }
      }
      buyerActivity {
        status
        updatedAt
        questions {
          question
          answer
          sources {
            type
            id
            url
            title
            chunkNumber
            documentType
            filePath
            isDeleted
            citationId
            locations {
              pageNumber
              bbox {
                x
                y
                width
                height
              }
            }
          }
        }
        relatedNotices {
          guid
          name
          stagev2
          expiryDate
          closeDate
          publishDate
          value
          procurementStage {
            id
            stage
          }
          lots {
            suppliers {
              guid
              name
            }
          }
        }
      }
      bid {
        id
        name
      }
      metadata {
        source
        leadType
        documentId
        motivation
        matchingText
        pageNumbers
        locations {
          pageNumber
          bbox {
            x
            y
            width
            height
          }
        }
      }
    }
  }
`);

const LEAD_SUPPLIER_BUYER_NOTICE_SUMMARY_FRAGMENT_QUERY = graphql(`
  query leadSupplierBuyerNoticeSummary(
    $id: String!
    $supplierBuyerNoticeSummaryRequest: SupplierBuyerNoticeSummaryRequest!
  ) {
    lead(id: $id) {
      supplierBuyerNoticeSummary(
        SupplierBuyerNoticeSummaryRequest: $supplierBuyerNoticeSummaryRequest
      ) {
        supplier {
          name
          id
          logoUrl
        }
        allAwards {
          totalCount
          totalValue
        }
        similarAwards {
          totalCount
          totalValue
        }
      }
    }
  }
`);

export type LeadDetails = LeadQuery["lead"];

export function useLead(
  id: string,
  options?: UseQueryOptions<LeadQuery, unknown, LeadQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(["lead", id], LEAD_QUERY, [{ id }], {
    ...options,
    ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
  });
  return { data: data?.lead, ...rest };
}

export function useLeadSupplierBuyerNoticeSummary(
  leadId: string,
  searchRequest: SupplierBuyerNoticeSummaryRequest,
  options?: UseQueryOptions<
    LeadSupplierBuyerNoticeSummaryQuery,
    unknown,
    LeadSupplierBuyerNoticeSummaryQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["lead", leadId, "supplierBuyerNoticeSummary", searchRequest],
    LEAD_SUPPLIER_BUYER_NOTICE_SUMMARY_FRAGMENT_QUERY,
    [{ id: leadId, supplierBuyerNoticeSummaryRequest: searchRequest }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.lead.supplierBuyerNoticeSummary,
    ...rest,
  };
}

export type UseLeadSupplierBuyerNoticeSummary = typeof useLeadSupplierBuyerNoticeSummary;
